<template>
  <div class="ov-page">
    <div class="ov-header">
      <div class="ov-note ov-note--info ov-header__note">
        <v-icon small>mdi-information-outline</v-icon>
        <span>Choisissez l'offre de voyage du client, puis le programme (date et heure de départ) pour réserver son billet.</span>
      </div>
      <div class="ov-header__actions">
        <button type="button" class="ov-btn ov-btn--secondary" :disabled="loading || isRefreshing" @click="refreshOffers">
          <v-progress-circular v-if="isRefreshing" indeterminate size="16" width="2"></v-progress-circular>
          <v-icon v-else small>mdi-refresh</v-icon>
          {{ $t('seller.refresh') }}
        </button>
      </div>
    </div>

    <div class="ov-card">
      <div class="ov-card__head">
        <div class="ov-card__title">
          Offres actives
          <span class="ov-count">{{ filteredOffers.length }}</span>
        </div>
        <div class="ov-filters">
          <select v-model="filterDepart" class="ov-select" aria-label="Départ">
            <option :value="null">Tous les départs</option>
            <option v-for="(depart, index) in uniqueDeparturesList" :key="index" :value="depart">{{ depart }}</option>
          </select>
          <select v-model="filterDestination" class="ov-select" aria-label="Destination">
            <option :value="null">Toutes les destinations</option>
            <option v-for="(dest, index) in uniqueDestinationsList" :key="index" :value="dest">{{ dest }}</option>
          </select>
          <select v-model="filterType" class="ov-select" aria-label="Type d'offre">
            <option :value="null">Tous les types</option>
            <option v-for="(type, index) in uniqueTypes" :key="index" :value="type">{{ type }}</option>
          </select>
          <div class="ov-search">
            <v-icon small>mdi-magnify</v-icon>
            <input v-model="search" type="text" class="ov-input" :placeholder="$t('seller.searchPlaceholder')" />
          </div>
          <button v-if="hasActiveFilters" type="button" class="sp-reset" @click="resetFilters">Effacer</button>
        </div>
      </div>

      <v-data-table
        class="ov-table sp-table"
        :headers="headers"
        :search="search"
        :loading="loading"
        loader-height="2"
        :items="filteredOffers"
        :items-per-page="10"
        :loading-text="$t('seller.loadingOffers')"
        :no-data-text="hasActiveFilters ? 'Aucune offre ne correspond aux filtres.' : $t('seller.noOfferAvailable')"
        @click:row="voirDetailBtn"
      >
        <template v-slot:[`item.designation`]="{ item }">
          <span class="sp-offer">{{ item.designation }}</span>
        </template>

        <template v-slot:[`item.villeDepartDesignation`]="{ item }">
          <div class="sp-route">
            <span>{{ item.villeDepartDesignation || '—' }}</span>
            <span class="sp-route__line"></span>
            <span>{{ item.villeDestinationDesignation || '—' }}</span>
          </div>
        </template>

        <template v-slot:[`item.typeOffreVoyageDesignation`]="{ item }">
          <span v-if="item.typeOffreVoyageDesignation" class="ov-badge ov-badge--navy ov-badge--plain">
            {{ item.typeOffreVoyageDesignation }}
          </span>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <button type="button" class="ov-btn ov-btn--primary ov-btn--sm" @click.stop="voirDetailBtn(item)">
            {{ $t('seller.viewPrograms') }}
            <v-icon small>mdi-arrow-right</v-icon>
          </button>
        </template>
      </v-data-table>
    </div>

    <v-alert class="myalert alert-error" type="error" width="350px" dismissible>{{ errorMsg }}</v-alert>
  </div>
</template>

<script>
import { offerService } from '@/services';
import { showErrorMessage } from "../messages/messageProcess";
export default {
  name: "SelectionnerUnProgramme",

  data() {
    return {
      loading: true,
      isRefreshing: false,

      errorMsg: "",
      warningMsg: "",
      successMsg: "",

      search: "",
      filterType: null,
      filterDepart: null,
      filterDestination: null,

      headers: [
        { text: this.$t('seller.reference'), value: "designation" },
        { text: "Trajet", value: "villeDepartDesignation" },
        { text: this.$t('seller.type'), value: "typeOffreVoyageDesignation" },
        { text: "", value: "actions", sortable: false, align: "end" },
        // Colonne masquée : gardée pour que la recherche porte aussi sur la destination
        { text: "", value: "villeDestinationDesignation", align: " d-none" },
      ],

      offreVoyageAvailableList: [],
      offreVoyageList: [],
    };
  },

  computed: {
    hasActiveFilters() {
      return !!(this.filterType || this.filterDepart || this.filterDestination || this.search);
    },

    uniqueTypes() {
      const types = new Set();
      this.offreVoyageAvailableList.forEach(offre => {
        if (offre.typeOffreVoyageDesignation) {
          types.add(offre.typeOffreVoyageDesignation);
        }
      });
      return Array.from(types).sort();
    },

    uniqueDestinationsList() {
      const destinations = new Set();
      this.offreVoyageAvailableList.forEach(offre => {
        if (offre.villeDestinationDesignation) {
          destinations.add(offre.villeDestinationDesignation);
        }
      });
      return Array.from(destinations).sort();
    },
    
    uniqueDeparturesList() {
      const departures = new Set();
      this.offreVoyageAvailableList.forEach(offre => {
        if (offre.villeDepartDesignation) {
          departures.add(offre.villeDepartDesignation);
        }
      });
      return Array.from(departures).sort();
    },

    filteredOffers() {
      let filtered = this.offreVoyageAvailableList;

      if (this.filterType) {
        filtered = filtered.filter(offre => offre.typeOffreVoyageDesignation === this.filterType);
      }

      if (this.filterDepart) {
        filtered = filtered.filter(offre => offre.villeDepartDesignation === this.filterDepart);
      }

      if (this.filterDestination) {
        filtered = filtered.filter(offre => offre.villeDestinationDesignation === this.filterDestination);
      }

      return filtered;
    },
  },

  methods: {
    /**
     * Obtenir la liste des offres de voyages disponibles avec cache
     * Le cache est géré automatiquement par offerService
     * @param {boolean} forceRefresh - Force le rechargement sans cache
     */
    async getOffreVoyageAvailables(forceRefresh = false) {
      this.loading = true;
      this.errorMsg = "";
      
      try {
        const companyName = this.$store.state.userAuthentified.compagnieTransportRaisonSociale;
        
        if (!companyName) {
          this.errorMsg = "Impossible de récupérer le nom de la compagnie";
          showErrorMessage();
          return;
        }

        // Appel au service avec cache intégré
        // Si forceRefresh = true, on bypass le cache
        const offers = await offerService.getOffersByCompany(companyName, forceRefresh);
        
        // Filtrer uniquement les offres actives
        this.offreVoyageList = offers;
        this.offreVoyageAvailableList = offers.filter(offer => offer.isActif === true);
        
        
      } catch (error) {
        this.errorMsg = error.message || this.$t('seller.error');
        showErrorMessage();
      } finally {
        this.loading = false;
        this.isRefreshing = false;
      }
    },

    /**
     * Rafraîchir manuellement les données (bypass le cache)
     */
    resetFilters() {
      this.filterType = null;
      this.filterDepart = null;
      this.filterDestination = null;
      this.search = "";
    },

    async refreshOffers() {
      this.isRefreshing = true;
      await this.getOffreVoyageAvailables(true);
    },

    storeOffreVoyageItem(item) {
      const parsedOffreSelected = JSON.stringify(item);
      localStorage.setItem("offreVoyageSelected", parsedOffreSelected);
    },

    reserverTicket(offreVoyage) {
      this.storeOffreVoyageItem(offreVoyage);
      this.$router.push({ path: "/reservationBillet" });
    },

    voirDetailBtn(offreVoyage) {
      this.storeOffreVoyageItem(offreVoyage);
      this.$router.push({ path: "/offre_voyage_details" });
    },
  },

  mounted() {
    this.getOffreVoyageAvailables();
  },
};
</script>

<style lang="scss" scoped>
@import "../../styles/admin/ui";

.sp-table ::v-deep tbody tr {
  cursor: pointer;
}

.sp-offer {
  font-weight: 800;
  color: $ov-navy;
}

.sp-route {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  color: $ov-ink;
  white-space: nowrap;
}

.sp-route__line {
  width: 22px;
  border-top: 2px dashed rgba(21, 158, 114, 0.55);
}

.sp-reset {
  border: none;
  background: none;
  padding: 0 4px;
  font-size: 13px;
  font-weight: 700;
  color: $ov-green-dark;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}
</style>
